import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Upload, Download, QrCode } from 'lucide-react';
import Papa from 'papaparse';

const AssetsPage = () => {
  const { isAdmin } = useAuth();
  const [assets, setAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const debounceRef = useRef(null);
  const fetchIdRef = useRef(0);

  const [formData, setFormData] = useState({
    asset_type: '',
    brand: '',
    model_name: '',
    serial_number: '',
    ram_size: '',
    ram_type: '',
    ssd_size: '',
    ssd_type: '',
    gpu: '',
    purchase_date: '',
    asset_status: 'Active',
  });

  const fetchAssets = useCallback(async (search = '') => {
    const thisId = ++fetchIdRef.current;
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      const response = await api.get('/assets', { params });
      if (thisId === fetchIdRef.current) {
        setAssets(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      if (thisId === fetchIdRef.current) {
        toast.error('Failed to load assets');
      }
    } finally {
      if (thisId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Single effect handles both initial load (0ms) and search changes (500ms debounce)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = searchTerm === '' && loading ? 0 : 500;
    debounceRef.current = setTimeout(() => {
      fetchAssets(searchTerm);
    }, delay);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm, fetchAssets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await api.put(`/assets/${editingAsset.asset_id}`, formData);
        toast.success('Asset updated successfully!');
      } else {
        await api.post('/assets', formData);
        toast.success('Asset created successfully!');
      }
      setShowDialog(false);
      resetForm();
      fetchAssets(searchTerm);
    } catch (error) {
      console.error('Failed to save asset:', error);
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      asset_type: asset.asset_type,
      brand: asset.brand,
      model_name: asset.model_name,
      serial_number: asset.serial_number,
      ram_size: asset.ram_size || '',
      ram_type: asset.ram_type || '',
      ssd_size: asset.ssd_size || '',
      ssd_type: asset.ssd_type || '',
      gpu: asset.gpu || '',
      purchase_date: asset.purchase_date || '',
      asset_status: asset.asset_status,
    });
    setShowDialog(true);
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      await api.delete(`/assets/${assetId}`);
      toast.success('Asset deleted successfully!');
      fetchAssets(searchTerm);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      toast.error('Failed to delete asset');
    }
  };

  const resetForm = () => {
    setFormData({
      asset_type: '',
      brand: '',
      model_name: '',
      serial_number: '',
      ram_size: '',
      ram_type: '',
      ssd_size: '',
      ssd_type: '',
      gpu: '',
      purchase_date: '',
      asset_status: 'Active',
    });
    setEditingAsset(null);
  };

  const handleBulkImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        setImporting(true);
        try {
          const response = await api.post('/assets/bulk-import', { rows: results.data });
          toast.success(`Imported ${response.data.imported} of ${response.data.total} assets`);
          if (response.data.errors.length > 0) {
            console.error('Import errors:', response.data.errors);
            toast.error(`${response.data.errors.length} errors occurred. Check console.`);
          }
          setShowBulkImport(false);
          fetchAssets(searchTerm);
        } catch (error) {
          console.error('Failed to import:', error);
          toast.error('Bulk import failed');
        } finally {
          setImporting(false);
        }
      },
      error: (error) => {
        toast.error('Failed to parse CSV file');
        console.error(error);
      },
    });
  };

  const downloadSampleCSV = () => {
    const sampleCSV = `asset_type,brand,model_name,serial_number,ram_size,ram_type,ssd_size,ssd_type,gpu,purchase_date,asset_status
Laptop,Dell,Latitude 5420,SN12345,16 GB,DDR4,512 GB,NVMe SSD,Intel UHD,2023-01-15,Active
Desktop,HP,EliteDesk 800,SN67890,32 GB,DDR4,1 TB,NVMe SSD,NVIDIA GTX 1650,2023-02-20,Active`;
    
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'assets_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleGenerateQR = (assetId) => {
    const qrUrl = `${process.env.REACT_APP_BACKEND_URL}/api/qr-code/${assetId}`;
    window.open(qrUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="assets-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Asset Master</h1>
          <p className="text-lg text-muted-foreground">Manage all rental assets</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
            <DialogTrigger asChild>
              <Button variant="outline" size="lg" className="h-14 px-6">
                <Upload className="w-5 h-5 mr-2" />
                Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Bulk Import Assets</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Upload a CSV file to import multiple assets at once
                </p>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-bold text-blue-900 mb-2">Required CSV Columns:</p>
                  <ul className="text-xs text-blue-900 space-y-1">
                    <li>• asset_type, brand, model_name, serial_number (required)</li>
                    <li>• ram_size, ram_type, ssd_size, ssd_type, gpu, purchase_date, asset_status (optional)</li>
                  </ul>
                </div>
                <Button variant="outline" className="w-full" onClick={downloadSampleCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Sample CSV
                </Button>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleBulkImport}
                  disabled={importing}
                  data-testid="bulk-import-input"
                />
                {importing && <p className="text-sm text-center text-muted-foreground">Importing...</p>}
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setShowDialog(true);
                }}
                data-testid="add-asset-button"
                size="lg"
                className="h-14 px-8 font-bold uppercase tracking-wide shadow-lg shadow-primary/20"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Asset
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Asset Type *</Label>
                    <Select value={formData.asset_type} onValueChange={(v) => setFormData({ ...formData, asset_type: v })} required>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Laptop">Laptop</SelectItem>
                        <SelectItem value="Desktop">Desktop</SelectItem>
                        <SelectItem value="Projector">Projector</SelectItem>
                        <SelectItem value="Tablet">Tablet</SelectItem>
                        <SelectItem value="TV">TV</SelectItem>
                        <SelectItem value="Printer">Printer</SelectItem>
                        <SelectItem value="PS5">PS5</SelectItem>
                        <SelectItem value="Air Purifier">Air Purifier</SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Brand *</Label>
                    <Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} required className="h-12" />
                  </div>
                  <div>
                    <Label>Model Name *</Label>
                    <Input value={formData.model_name} onChange={(e) => setFormData({ ...formData, model_name: e.target.value })} required className="h-12" />
                  </div>
                  <div>
                    <Label>Serial Number *</Label>
                    <Input value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} required className="h-12" />
                  </div>
                  {['Laptop', 'Desktop'].includes(formData.asset_type) && (
                    <>
                      <div>
                        <Label>RAM Size</Label>
                        <Select value={formData.ram_size} onValueChange={(v) => setFormData({ ...formData, ram_size: v })}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select RAM" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="8 GB">8 GB</SelectItem>
                            <SelectItem value="16 GB">16 GB</SelectItem>
                            <SelectItem value="32 GB">32 GB</SelectItem>
                            <SelectItem value="128 GB">128 GB</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>RAM Type</Label>
                        <Select value={formData.ram_type} onValueChange={(v) => setFormData({ ...formData, ram_type: v })}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="DDR3">DDR3</SelectItem>
                            <SelectItem value="DDR4">DDR4</SelectItem>
                            <SelectItem value="DDR5">DDR5</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>SSD Size</Label>
                        <Select value={formData.ssd_size} onValueChange={(v) => setFormData({ ...formData, ssd_size: v })}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select SSD" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="128 GB">128 GB</SelectItem>
                            <SelectItem value="256 GB">256 GB</SelectItem>
                            <SelectItem value="512 GB">512 GB</SelectItem>
                            <SelectItem value="1 TB">1 TB</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>SSD Type</Label>
                        <Select value={formData.ssd_type} onValueChange={(v) => setFormData({ ...formData, ssd_type: v })}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SATA SSD">SATA SSD</SelectItem>
                            <SelectItem value="NVMe SSD">NVMe SSD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>GPU</Label>
                        <Input value={formData.gpu} onChange={(e) => setFormData({ ...formData, gpu: e.target.value })} className="h-12" />
                      </div>
                    </>
                  )}
                  <div>
                    <Label>Purchase Date</Label>
                    <Input type="date" value={formData.purchase_date} onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} className="h-12" />
                  </div>
                  <div>
                    <Label>Asset Status</Label>
                    <Select value={formData.asset_status} onValueChange={(v) => setFormData({ ...formData, asset_status: v })}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Under Repair">Under Repair</SelectItem>
                        <SelectItem value="Retired">Retired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-asset-button">
                    {editingAsset ? 'Update' : 'Create'} Asset
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* SEARCH BAR (Fixed: Now there is only one) */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <Input
            data-testid="search-assets-input"
            placeholder="Search by Asset ID, Serial Number, Model, or Brand..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 h-14 text-lg"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        {assets.length === 0 ? (
          <p className="text-center py-12 text-muted-foreground">No assets found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Asset ID</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Brand / Model</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Serial Number</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Status</th>
                  {isAdmin && <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {assets.map((asset, index) => (
                  <tr key={asset.asset_id || asset.id || index} data-testid={`asset-row-${asset.asset_id || index}`} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4 font-bold text-primary">{asset.asset_id || '-'}</td>
                    <td className="px-6 py-4">{asset.asset_type || '-'}</td>
                    <td className="px-6 py-4">
                      {[asset.brand, asset.model_name].filter(Boolean).join(' ') || '-'}
                    </td>
                    <td className="px-6 py-4 font-mono text-sm">{asset.serial_number || '-'}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                          asset.asset_status === 'Active' || asset.asset_status === 'Available'
                            ? 'bg-green-100 text-green-700'
                            : asset.asset_status === 'Under Repair' || asset.asset_status === 'In Transit'
                            ? 'bg-yellow-100 text-yellow-700'
                            : asset.asset_status === 'Deployed'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {asset.asset_status || 'Unknown'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(asset)} data-testid={`edit-asset-${asset.asset_id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleGenerateQR(asset.asset_id)} data-testid={`qr-asset-${asset.asset_id}`}>
                            <QrCode className="w-4 h-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDelete(asset.asset_id)} data-testid={`delete-asset-${asset.asset_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetsPage;